<template>
  <div class="media-carousel">
    <v-tabs-items v-model="tab">
      <v-tab-item
        v-for="(tab, idx) in mediaListParts"
        :key="idx"
        class="media-carousel__tab"
      >
        <div v-for="item in tab" :key="item.key" class="media-carousel__photo">
          <div class="media-carousel__photo-uploader">
            <video-uploader
              v-if="item.is_video"
              :label="item.name"
              :video="media[item.key]"
              :readonly="readonly"
              @update:video="
                $emit('update:media', { ...media, [item.key]: $event })
              "
            />

            <image-uploader
              v-else
              :label="item.name"
              :photo="media[item.key]"
              :readonly="readonly"
              @update:photo="
                $emit('update:media', { ...media, [item.key]: $event })
              "
            />

            <v-tooltip
              v-if="isPrevStateBtnVisible(item)"
              color="warning"
              content-class="comment-tooltip"
              max-width="250"
              left
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  class="media-carousel__prev-photo-btn"
                  color="warning"
                  small
                  icon
                  @click="setPreviousPhoto(item.key)"
                >
                  <v-icon>mdi-history</v-icon>
                </v-btn>
              </template>

              {{ $t('mechanic.prevPhoto') }}
            </v-tooltip>
          </div>

          <v-checkbox
            class="media-carousel__has-damage"
            color="warning"
            :disabled="!media[item.key] || readonly"
            :label="$t('mechanic.autoHasDamage')"
            hide-details
            @change="
              $emit('update:media', {
                ...media,
                [item.key]: { ...media[item.key], hasDamage: $event },
              })
            "
          />

          <v-textarea
            class="media-carousel__comment"
            height="50"
            no-resize
            outlined
            dense
            :disabled="!media[item.key]"
            :label="$t('common.fields.comment')"
            :value="comments[item.key]"
            :readonly="readonly"
            :rules="getRules(media[item.key])"
            @input="
              $emit('update:comments', { ...comments, [item.key]: $event })
            "
          />
        </div>
      </v-tab-item>

      <v-tab-item class="media-carousel__tab">
        <div
          v-for="(photo, idx) in additionalPhoto"
          :key="photo.key"
          class="media-carousel__photo"
        >
          <div class="media-carousel__photo-uploader">
            <image-uploader
              :label="`${$t('auto.labels.additionalPhoto')} ${idx + 1}`"
              :photo="additionalPhoto[idx]"
              :readonly="readonly"
              @update:photo="
                $emit(
                  'update:additionalPhoto',
                  onAdditionalPhotoUpdate($event, idx),
                )
              "
            />
          </div>
        </div>

        <div
          v-if="readonly ? additionalPhoto.length === 0 : true"
          class="media-carousel__photo"
        >
          <div class="media-carousel__photo-uploader">
            <image-uploader
              :label="
                `${$t('auto.labels.additionalPhoto')} ${additionalPhoto.length +
                  1}`
              "
              :photo="additionalPhoto[additionalPhoto.length]"
              :readonly="readonly"
              @update:photo="
                $emit(
                  'update:additionalPhoto',
                  onAdditionalPhotoUpdate($event, additionalPhoto.length),
                )
              "
            />
          </div>
        </div>

        <div class="media-carousel__additional-comment">
          <v-textarea
            height="50"
            no-resize
            outlined
            :readonly="readonly"
            :value="additionalPhotoComment"
            :label="$t('auto.labels.additionalPhotoComment')"
            @input="$emit('update:additional-photo-comment', $event)"
          />
        </div>
      </v-tab-item>

      <v-tab-item class="media-carousel__tab">
        <div
          v-for="(video, idx) in additionalVideo"
          :key="video.key"
          class="media-carousel__photo"
        >
          <div class="media-carousel__photo-uploader">
            <video-uploader
              :label="`${$t('auto.labels.additionalVideo')} ${idx + 1}`"
              :video="additionalVideo[idx]"
              :readonly="readonly"
              @update:video="onAdditionalVideoUpdate($event, idx)"
            />
          </div>
        </div>

        <div
          v-if="readonly ? additionalVideo.length === 0 : true"
          class="media-carousel__photo"
        >
          <div class="media-carousel__photo-uploader">
            <video-uploader
              :label="
                `${$t('auto.labels.additionalVideo')} ${additionalVideo.length +
                  1}`
              "
              :video="additionalVideo[additionalVideo.length]"
              :readonly="readonly"
              @update:video="
                onAdditionalVideoUpdate($event, additionalVideo.length)
              "
            />
          </div>
        </div>

        <div class="media-carousel__additional-comment">
          <v-textarea
            height="50"
            no-resize
            outlined
            :readonly="readonly"
            :value="additionalVideoComment"
            :label="$t('auto.labels.additionalVideoComment')"
            @input="$emit('update:additional-video-comment', $event)"
          />
        </div>
      </v-tab-item>
    </v-tabs-items>

    <div class="media-carousel__tabs-controls-wrapper">
      <button
        type="button"
        class="media-carousel__tabs-controls-arrow"
        :disabled="tab === 0"
        @click="$emit('update:tab', tab - 1)"
      >
        <v-icon size="25">mdi-chevron-left</v-icon>
      </button>

      <div class="media-carousel__tabs-controls">
        <button
          v-for="num in tabsCount"
          :key="num"
          type="button"
          :data-value="num"
          :class="{ active: tab === num - 1 }"
          @click="$emit('update:tab', num - 1)"
        />
      </div>

      <button
        type="button"
        class="media-carousel__tabs-controls-arrow"
        :disabled="tab === mediaListParts.length + 1"
        @click="$emit('update:tab', tab + 1)"
      >
        <v-icon size="25">mdi-chevron-right</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import ImageUploader from '@/components/shared/ImageUploader.vue';
import VideoUploader from '@/components/shared/VideoUploader.vue';

export default {
  components: { ImageUploader, VideoUploader },
  props: {
    tab: {
      type: Number,
      default: 0,
    },
    media: {
      type: Object,
      default: () => ({}),
    },
    comments: {
      type: Object,
      default: () => ({}),
    },
    additionalPhoto: {
      type: Array,
      default: () => [],
    },
    additionalVideo: {
      type: Array,
      default: () => [],
    },
    additionalPhotoComment: {
      type: String,
      default: '',
    },
    additionalVideoComment: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    stateMedia() {
      return this.$store.state.auto.autosState.media;
    },
    mediaListParts() {
      const media = Object.values(this.stateMedia);
      const list = [];
      const step = 4;

      for (let startIdx = 0; startIdx < media.length; startIdx += step) {
        list.push(media.slice(startIdx, startIdx + step));
      }

      return list;
    },
    tabsCount() {
      return this.mediaListParts.length + 2;
    },
    lastStateMedia() {
      return this.$store.state.mechanic.autoLastState.photo;
    },
  },
  methods: {
    isRequired(v) {
      return !!v || this.$t('common.fields.validation.field.required');
    },

    getRules(media) {
      const rules = [];

      if (media && media.hasDamage) {
        rules.push(this.isRequired);
      }

      return rules;
    },

    onAdditionalPhotoUpdate(photo, idx) {
      const data = [...this.additionalPhoto];

      data[idx] = photo;
      data.filter((item) => Boolean(item));

      this.$emit('update:additional-photo', data);
    },

    onAdditionalVideoUpdate(video, idx) {
      const data = [...this.additionalVideo];

      data[idx] = video;
      data.filter((item) => Boolean(item));

      this.$emit('update:additional-video', data);
    },

    isPrevStateBtnVisible(item) {
      const { lastStateType } = this.$store.state.mechanic;
      /* eslint-disable */
      return (
        !item.is_video &&
        !this.media[item.key] &&
        !this.readonly &&
        this.lastStateMedia[item.key] &&
        lastStateType === 0
      );
      /* eslint-enable */
    },

    setPreviousPhoto(key) {
      const photo = {
        key: this.lastStateMedia[key].key,
        link: this.lastStateMedia[key].link,
        name: this.lastStateMedia[key].value,
      };

      this.$emit('update:media', { ...this.media, [key]: photo });
    },
  },
};
</script>

<style lang="scss" scoped>
.media-carousel {
  &__tab {
    display: flex;
    flex-wrap: wrap;
  }

  &__photo {
    width: 50%;
    padding: 10px;

    &-uploader {
      height: 150px;
      position: relative;
    }
  }

  &__prev-photo-btn {
    position: absolute;
    bottom: 10px;
    left: 10px;
  }

  &__tabs-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 15px;
    border-radius: 15px;
    background-color: rgba(46, 46, 46, 0.5);

    &-wrapper {
      display: flex;
      justify-content: center;
    }

    &-arrow {
      margin: 0 5px;
      transition: opacity 0.3s;

      &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &:hover {
        opacity: 0.6;
      }
    }

    button {
      opacity: 0.5;
      background: none;
      border: none;
      box-shadow: none;
      outline: none;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #fff;

      &:not(:last-child) {
        margin-right: 5px;
      }

      &.active {
        opacity: 1;
      }
    }
  }

  &__comment {
    ::v-deep textarea {
      padding: 3px 0;
      font-size: 14px !important;
      line-height: 1.4;
    }

    ::v-deep .v-label {
      font-size: 15px;
    }
  }

  &__has-damage {
    margin-top: 10px;
    margin-bottom: 15px;

    ::v-deep .v-label {
      font-size: 15px;
    }
  }

  &__additional-comment {
    padding: 0 10px;
    margin-top: 15px;
    width: 100%;

    ::v-deep .v-label {
      font-size: 15px;
    }

    ::v-deep textarea {
      font-size: 15px;
      line-height: 1.3;
    }

    ::v-deep
      .v-input:not(.v-input--is-focused):not(.v-input--is-label-active)
      .v-label {
      top: 10px !important;
    }
  }

  ::v-deep .video-uploader__no-data,
  ::v-deep .video-uploader__upload,
  ::v-deep .no-photo,
  ::v-deep .upload-icon {
    font-size: 15px !important;
    font-weight: 500;
    color: #464646;
    padding-top: 15px;
  }

  ::v-deep .v-icon {
    font-size: 30px !important;
  }
}
</style>
